(() => {
  const gallerySelector = '.product-media-container .splide--product';
  const purchaseSelector = '.product-content-container .add-to-cart-container';

  const initializeGallery = (gallery) => {
    if (gallery.dataset.mamboGalleryReady === 'true') return;

    const track = gallery.querySelector('.splide__track');
    const list = track?.querySelector('.splide__list');
    const slides = list ? Array.from(list.children).filter((item) => item.classList.contains('splide__slide')) : [];
    if (!track || slides.length < 2) return;

    const thumbnails = gallery.querySelector('[data-mambo-thumbnails]') || document.createElement('div');
    const serverButtons = Array.from(thumbnails.querySelectorAll('[data-mambo-thumbnail-index]'));
    if (serverButtons.length && serverButtons.length !== slides.length) return;
    thumbnails.className = 'mambo-pdp-thumbnails';
    thumbnails.setAttribute('role', 'group');
    thumbnails.setAttribute('aria-label', 'Choose product image');

    const buttons = slides.map((slide, index) => {
      const sourceImage = slide.querySelector('img');
      if (!sourceImage) return null;

      const button = serverButtons[index] || document.createElement('button');
      const slideId = slide.id || `mambo-product-media-${index + 1}`;

      slide.id = slideId;
      button.type = 'button';
      button.className = 'mambo-pdp-thumbnail';
      button.setAttribute('aria-label', `View product image ${index + 1} of ${slides.length}`);
      button.setAttribute('aria-controls', slideId);
      if (!serverButtons.length) {
        const thumbnail = document.createElement('img');
        const thumbnailUrl = new URL(sourceImage.currentSrc || sourceImage.src, window.location.href);
        thumbnailUrl.searchParams.set('width', '320');
        thumbnail.src = thumbnailUrl.href;
        thumbnail.alt = '';
        thumbnail.loading = 'lazy';
        thumbnail.decoding = 'async';
        thumbnail.width = 320;
        thumbnail.height = 320;
        button.appendChild(thumbnail);
        thumbnails.appendChild(button);
      }
      return button;
    });

    if (buttons.some((button) => !button)) return;

    const imageStatus = document.createElement('p');
    imageStatus.className = 'sr-only';
    imageStatus.setAttribute('role', 'status');
    let selectionVersion = 0;

    const waitForImage = (image) => new Promise((resolve) => {
      if (image.complete && image.naturalWidth > 0) {
        resolve(true);
        return;
      }
      let timeout;
      const finish = (loaded) => {
        window.clearTimeout(timeout);
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        resolve(loaded);
      };
      const onLoad = () => finish(image.naturalWidth > 0);
      const onError = () => finish(false);
      image.addEventListener('load', onLoad, { once: true });
      image.addEventListener('error', onError, { once: true });
      timeout = window.setTimeout(() => finish(false), 15000);
      image.loading = 'eager';
      image.fetchPriority = 'high';
      if (image.complete && image.naturalWidth > 0) finish(true);
    });

    const activate = async (activeIndex) => {
      const image = slides[activeIndex]?.querySelector('img');
      if (!image) return;
      const version = ++selectionVersion;
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
      buttons[activeIndex].setAttribute('aria-busy', 'true');
      imageStatus.textContent = 'Loading product image.';
      let loaded = await waitForImage(image);
      if (loaded && typeof image.decode === 'function') {
        try { await image.decode(); } catch { loaded = image.complete && image.naturalWidth > 0; }
      }
      if (version !== selectionVersion || !gallery.isConnected) return;
      buttons[activeIndex].removeAttribute('aria-busy');
      if (!loaded) {
        imageStatus.textContent = 'Image could not be loaded. Please try again.';
        return;
      }
      if (gallery.hasAttribute('data-mambo-gallery') && image.naturalHeight > 0) {
        track.style.setProperty('--mambo-gallery-ratio', String(image.naturalWidth / image.naturalHeight));
      }
      slides.forEach((slide, index) => {
        const active = index === activeIndex;
        slide.classList.toggle('is-mambo-gallery-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        buttons[index].setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      imageStatus.textContent = '';
    };

    buttons.forEach((button, index) => button.addEventListener('click', () => activate(index)));
    if (!thumbnails.isConnected) track.insertAdjacentElement('afterend', thumbnails);
    thumbnails.insertAdjacentElement('afterend', imageStatus);
    // Keep an initial slide visible until the requested image is ready.
    (slides.find((slide) => slide.classList.contains('is-mambo-gallery-active')) || slides[0]).classList.add('is-mambo-gallery-active');
    gallery.classList.add('mambo-pdp-gallery-ready');
    gallery.dataset.mamboGalleryReady = 'true';
    const productRoot = gallery.closest('data-island[src="product"]');
    let activeGroup;
    const showMediaGroup = (mediaId) => {
      const group = mediaId == null ? '' : String(mediaId);
      if (group === activeGroup) return;
      const matching = slides.map((slide, index) => ({ slide, index }))
        .filter(({ slide }) => slide.dataset.mediaGroupId === group || /^Mambo T-shirt size guide/i.test(slide.querySelector('img')?.alt || ''));
      const visible = matching.length ? matching.map(({ index }) => index) : slides.map((_, index) => index);
      buttons.forEach((button, index) => {
        const position = visible.indexOf(index);
        button.hidden = position === -1;
        if (position !== -1) button.setAttribute('aria-label', `View product image ${position + 1} of ${visible.length}`);
      });
      activeGroup = group;
      activate(visible[0]);
    };
    const onVariantChange = (event) => {
      if (!gallery.isConnected) {
        document.removeEventListener('theme:variant:change', onVariantChange);
        return;
      }
      if (event.detail?.productRootEl !== productRoot) return;
      const mediaId = event.detail.variant?.featured_media?.id;
      if (mediaId != null) showMediaGroup(mediaId);
    };
    document.addEventListener('theme:variant:change', onVariantChange);
    showMediaGroup(productRoot?.querySelector('[data-mambo-gallery-initial-media]')?.dataset.mamboGalleryInitialMedia);
  };

  const initializePurchaseBar = (actions) => {
    if (actions.dataset.mamboPurchaseReady === 'true') return;

    const anchor = document.createElement('div');
    anchor.className = 'mambo-pdp-purchase-anchor';
    let scrollFrame;

    actions.insertAdjacentElement('beforebegin', anchor);
    anchor.appendChild(actions);

    const syncAnchorHeight = () => {
      if (actions.classList.contains('is-mambo-purchase-sticky')) return;
      anchor.style.minHeight = '0px';
      anchor.style.minHeight = `${Math.ceil(actions.getBoundingClientRect().height)}px`;
    };

    const syncDesktopOffset = () => {
      const visibleHeaderBottom = Array.from(document.querySelectorAll('header'))
        .map((header) => header.getBoundingClientRect())
        .filter((bounds) => bounds.top <= 1 && bounds.bottom > 0 && bounds.height < 300)
        .reduce((bottom, bounds) => Math.max(bottom, bounds.bottom), 0);
      actions.style.setProperty('--mambo-purchase-top', `${Math.ceil(visibleHeaderBottom)}px`);
    };

    const updateVisibility = () => {
      syncDesktopOffset();
      const hasPassedPurchaseArea = anchor.getBoundingClientRect().bottom < 0;
      actions.classList.toggle('is-mambo-purchase-sticky', hasPassedPurchaseArea);
      actions.setAttribute('data-mambo-sticky-active', hasPassedPurchaseArea ? 'true' : 'false');
    };

    document.addEventListener('change', () => {
      window.requestAnimationFrame(() => {
        syncAnchorHeight();
        updateVisibility();
      });
    }, true);

    window.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = window.requestAnimationFrame(() => {
        updateVisibility();
        scrollFrame = undefined;
      });
    }, { passive: true });

    window.addEventListener('resize', () => {
      window.requestAnimationFrame(() => {
        syncAnchorHeight();
        updateVisibility();
      });
    }, { passive: true });

    window.addEventListener('pageshow', () => {
      syncAnchorHeight();
      updateVisibility();
    });

    const resizeObserver = new ResizeObserver(syncAnchorHeight);
    resizeObserver.observe(actions);
    actions.dataset.mamboPurchaseReady = 'true';
    window.requestAnimationFrame(() => {
      syncAnchorHeight();
      updateVisibility();
    });
  };

  const observedPurchaseRoots = new WeakSet();

  const initializeAll = (root = document) => {
    root.querySelectorAll(gallerySelector).forEach(initializeGallery);
    root.querySelectorAll(purchaseSelector).forEach(initializePurchaseBar);

    // Baseline 5.4 replaces the buy-button fragment after a variant change.
    root.querySelectorAll('.product-content-container').forEach((container) => {
      if (observedPurchaseRoots.has(container)) return;
      observedPurchaseRoots.add(container);
      let refreshFrame;
      const observer = new MutationObserver(() => {
        if (refreshFrame) return;
        refreshFrame = window.requestAnimationFrame(() => {
          refreshFrame = undefined;
          if (!container.isConnected) {
            observer.disconnect();
            return;
          }
          container.querySelectorAll('.add-to-cart-container').forEach(initializePurchaseBar);
        });
      });
      observer.observe(container, { childList: true, subtree: true });
    });
  };

  const isPurchaseLink = (link) => {
    const href = link.getAttribute('href') || '';
    if (!href || /^(javascript:|#)/i.test(href)) return true;
    const url = new URL(href, window.location.href);
    return (url.origin === window.location.origin && /\/(cart|checkout|checkouts)(\/|$)/.test(url.pathname)) ||
      Boolean(link.closest('.ks-chart-container, .kiwi-sizing, [data-kiwi-sizing], .shopify-payment-button, shopify-accelerated-checkout'));
  };

  const syncInformationLink = (link) => {
    if (!link.isConnected) return;
    // Same-page section shortcuts act as controls; destination links open a tab.
    if (link.matches('main a[href^="#Mambo"]')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = link.className;
      button.dataset.mamboScrollTarget = link.getAttribute('href').slice(1);
      while (link.firstChild) button.appendChild(link.firstChild);
      link.replaceWith(button);
      return;
    }
    if (isPurchaseLink(link)) return;
    link.target = '_blank';
    const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
    rel.add('noopener');
    link.setAttribute('rel', Array.from(rel).join(' '));
  };

  const collectInformationLinks = (root, links) => {
    if (root.nodeType !== 1 || !root.isConnected) return;
    if (root.matches('a[href]')) links.add(root);
    root.querySelectorAll('a[href]').forEach((link) => links.add(link));
  };

  document.addEventListener('click', (event) => {
    const control = event.target.closest?.('[data-mambo-scroll-target]');
    if (!control) return;
    const target = document.getElementById(control.dataset.mamboScrollTarget);
    target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  });

  const initializeLinkBehavior = () => {
    document.querySelectorAll('a[href]').forEach(syncInformationLink);
    const pendingLinks = new Set();
    let linkFrame;
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'attributes') {
          if (record.target.matches('a[href]')) pendingLinks.add(record.target);
        } else {
          record.addedNodes.forEach((node) => collectInformationLinks(node, pendingLinks));
        }
      });
      if (!pendingLinks.size || linkFrame) return;
      linkFrame = window.requestAnimationFrame(() => {
        linkFrame = undefined;
        pendingLinks.forEach(syncInformationLink);
        pendingLinks.clear();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { initializeAll(); initializeLinkBehavior(); }, { once: true });
  } else {
    initializeAll();
    initializeLinkBehavior();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();

